<script lang="ts">
  import { Splide, SplideSlide, SplideTrack } from "@splidejs/svelte-splide";
  import type { Splide as SplideInstance } from "@splidejs/splide";
  import "@splidejs/splide/css";
  import { onMount } from "svelte";
  import { translate } from "$lib/i18n";

  let splide: SplideInstance;

  // List of buzz images
  const buzzImages = [
    { src: "/buzz/Untitled-1.png", alt: "Partner 1", link: "https://coincodex.com/article/58008/forzabet-casino-review/" },
    { src: "/buzz/Untitled-2.png", alt: "Partner 2", link: "https://bestcasinorank.com/review/forzabet/" },
    { src: "/buzz/Untitled-3.png", alt: "Partner 3", link: "https://mr-gamble.com/en/online-casino/forzabet-casino/" },
    { src: "/buzz/Untitled-5.png", alt: "Partner 5", link: "https://99bitcoins.com/best-bitcoin-casino/forza-bet-review/" },
    { src: "/buzz/Untitled-6.png", alt: "Partner 6" },
    { src: "/buzz/Untitled-7.png", alt: "Partner 7" }
  ];

  // Splide carousel options
  const splideOptions = {
    type: 'loop',
    perPage: 7,
    perMove: 1,
    gap: '1rem',
    autoplay: true,
    interval: 3000,
    pauseOnHover: true,
    pagination: false,
    arrows: false, // Set to false as we'll use custom arrows
    lazyLoad: true,
    drag: true,
    padding: {
      left: '0px',
      right: '24px',
    },
    breakpoints: {
      640: {
        perPage: 2,
        gap: '0.5rem',
      },
      768: {
        perPage: 4,
        gap: '0.5rem',
      },
      1024: {
        perPage: 5,
        gap: '1rem',
      },
    },
  };

  function onMounted(event: CustomEvent<{ splide: SplideInstance }>) {
    splide = event.detail.splide;
  }

  onMount(() => {
    // Any initialization if needed
  });
</script>

<div class="buzz-about-us py-10 bg-primary-blue/10 mt-10">
  <Splide on:mounted={onMounted} aria-label="Buzz About Us" hasTrack={false} options={splideOptions}>
    <div class="flex justify-between mb-6 items-center">
      <h2 class="text-2xl font-bold text-primary-white gradient-text-fire">
        {$translate('Buzz About Us')}
      </h2>

      <!-- Navigation arrows in top right corner -->
      <div class="flex space-x-2 z-10">
        <button on:click={() => splide?.go('<')} class="custom-arrow border-2 border-primary-lightBlue rounded-full hover:bg-primary-lightBlue">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-primary-lightBlue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button on:click={() => splide?.go('>')} class="custom-arrow border-2 border-primary-lightBlue rounded-full hover:bg-primary-lightBlue">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-primary-lightBlue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>

    <SplideTrack>
      {#each buzzImages as image}
        <SplideSlide>
          {#if image.link}
            <a href={image.link} target="_blank" rel="noopener noreferrer" class="aspect-video border border-primary-lightBlue bg-primary-dark rounded-xl flex flex-col justify-between items-center overflow-hidden">
              <div class="p-2 h-full flex items-center">
                <img 
                  src={image.src} 
                  alt={image.alt} 
                  class="logo-image"
                  loading="lazy"
                />
              </div>
            </a>
          {:else}
            <div class="aspect-video border border-primary-lightBlue bg-primary-dark rounded-xl flex flex-col justify-between items-center overflow-hidden">
              <div class="p-2 h-full flex items-center">
                <img 
                  src={image.src} 
                  alt={image.alt} 
                  class="logo-image"
                  loading="lazy"
                />
              </div>
            </div>
          {/if}
        </SplideSlide>
      {/each}
    </SplideTrack>
  </Splide>
</div>

<style lang="scss">
  .buzz-about-us {
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%);
    }

    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%);
    }
  }

  /* Custom arrow styling */
  .custom-arrow {
    transition: all 0.2s ease;
    padding: 6px;
    background-color: rgba(13, 17, 28, 0.7); /* Semi-transparent dark background */
  }

  .custom-arrow:active {
    transform: scale(0.9);
  }

  .custom-arrow:hover {
    background-color: var(--primary-lightBlue);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  .custom-arrow:hover svg {
    stroke: white;
  }

  /* Logo image styling */
  .logo-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }

  .logo-image:hover {
    transform: scale(1.05);
  }

  /* Ensure the gradient text effect is applied */
  :global(.gradient-text-fire) {
    background: linear-gradient(90deg, #FF4D4D 0%, #F9CB28 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    display: inline-block;
  }
</style>
